import { logger } from '@disy/lib/logging';

import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeModificationDialogOptions } from 'cadenza/workbook/attribute-modification-dialog/attribute-modification-dialog';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getAttributeById } from 'cadenza/workbook/workbook-core-selectors.state';
import { createEditCalculatedAttributeAction } from 'cadenza/workbook/workbook-panel/databrowser/calculation-attributes/edit-calculated-attribute-action';

import i18n from './edit-attribute.properties';

export type EditAttributeOptions = Pick<
  AttributeModificationDialogOptions,
  'initiallyShowPresentationTab' | 'readOnly' | 'customInfoMessage'
>;

/**
 * Shows a modification dialog for the given active attribute.
 *
 * @param activeAttributeId - The ID of the attribute to modify
 * @param dataViewId - The data view id for the modification
 * @param options - Options object
 */
export function editActiveAttribute(
  activeAttributeId: ActiveAttributeId,
  dataViewId: DataViewId,
  options: EditAttributeOptions = {},
) {
  editAttributeInternal(activeAttributeId, {
    dataViewId,
    ...options,
    title: i18n('activeAttribute.dialog.title'),
    scope: 'VIEW',
  });
}

/**
 * Shows a modification dialog for the given attribute.
 *
 * @param attributeId - The ID of the attribute to modify
 * @param options - Options object
 */
function editAttribute(attributeId: AttributeId, options: EditAttributeOptions = {}) {
  editAttributeInternal(attributeId, {
    ...options,
    title: i18n('attribute.dialog.title'),
    scope: 'WORKBOOK',
  });
}

function editAttributeInternal(
  attributeId: AttributeId | ActiveAttributeId,
  options: AttributeModificationDialogOptions,
) {
  import('./attribute-modification-dialog')
    .then(({ AttributeModificationDialog }) => {
      new AttributeModificationDialog(attributeId, options).connectAndShow();
    })
    .catch((error) => logger.error('Failed to load attribute modifications dialog', error));
}

export function createEditAttributeAction(attributeId: AttributeId) {
  if (getAttributeById(getStore().getState(), attributeId).localAttributeType === 'CALCULATED_ATTRIBUTE') {
    return createEditCalculatedAttributeAction(attributeId);
  }

  return {
    label: i18n('attribute.action.label'),
    execute: () => editAttribute(attributeId),
  };
}
